import React, { useMemo } from 'react';
import { Formik, Form } from 'formik';
import { useTranslation } from 'localization';
import * as yup from 'yup';
import { Sphere } from 'types/global';
import { Schema, SchemaPerspective } from 'types/schema';
import type { PermissionList } from 'types/permissions';
import { generateYupValidationSchema } from '@center-platform/spheres/validation';
import SpherePropertiesMolecule from '../SphereProperties/SphereProperties';
import PerspectiveAssignmentsPanel, {
  PerspectiveAssignment,
} from '../../organisms/SphereEditor/PerspectiveAssignmentsPanel';
import perspectiveToSchemaProperty from '../../organisms/SphereEditor/PerspectiveTransform';
import DividerAtom from '../../atoms/Divider/DividerAtom';
import PrimeReactButton from '../../atoms/Button/ButtonAtom';
import './SphereInlineEditor.scss';

export interface SphereInlineEditorProps {
  sphere: Sphere;
  schema: Schema;
  editable?: boolean;
  parentSphereType?: string;
  organizationId?: string;
  /**
   * The viewer's already-fetched permission list for this sphere. Without it (and without
   * `writeBlockedProps`) every write-gated property renders locked (fail closed).
   */
  permissionList?: PermissionList;
  /** Property names the viewer cannot write; when absent it is computed from `permissionList`. */
  writeBlockedProps?: Set<string>;
  onSave?: (values: {
    properties: Record<string, unknown>;
    assignedPerspectives: PerspectiveAssignment[];
  }) => void;
  onCancel?: () => void;
}

interface FormValues {
  properties: Record<string, unknown>;
  assignedPerspectives: PerspectiveAssignment[];
}

const SphereInlineEditor: React.FC<SphereInlineEditorProps> = ({
  sphere,
  schema,
  editable = true,
  parentSphereType,
  organizationId,
  permissionList,
  writeBlockedProps,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const perspectives = schema.perspectives || [];
  const requiredPerspectives = perspectives.filter(
    (p: SchemaPerspective) => p.required
  );

  const perspectiveFields = useMemo(() => {
    return requiredPerspectives.reduce(
      (acc: Record<string, string>, persp: SchemaPerspective) => {
        const assignment = sphere.assignedPerspectives?.find(
          (ap) =>
            ap?.perspectiveProperties?.system_name === persp.system_name
        );
        acc[persp.system_name] =
          assignment?.assigned?.[0]?.sphere?.id ?? '';
        return acc;
      },
      {}
    );
  }, [requiredPerspectives, sphere.assignedPerspectives]);

  const perspectiveAsSchemaProps = useMemo(
    () => requiredPerspectives.map(perspectiveToSchemaProperty),
    [requiredPerspectives]
  );

  const allProperties = useMemo(
    () => [
      ...(schema.properties || []),
      ...perspectiveAsSchemaProps,
    ],
    [schema.properties, perspectiveAsSchemaProps]
  );

  const initialValues: FormValues = useMemo(
    () => ({
      properties: {
        ...(sphere.properties as unknown as Record<string, unknown>),
        ...perspectiveFields,
      },
      assignedPerspectives: (sphere.assignedPerspectives ||
        []) as PerspectiveAssignment[],
    }),
    [sphere.properties, perspectiveFields, sphere.assignedPerspectives]
  );

  const validationSchema = useMemo(() => {
    const generatedFields = generateYupValidationSchema(allProperties);
    return yup.object({ properties: yup.object(generatedFields) });
  }, [allProperties]);

  const handleSubmit = (values: FormValues) => {
    onSave?.(values);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ handleSubmit: formikSubmit }) => (
        <Form
          onSubmit={formikSubmit}
          className="sphere-inline-editor"
        >
          <SpherePropertiesMolecule
            sphere={sphere}
            schema={schema}
            editable={editable}
            parentSphereType={parentSphereType}
            organizationId={organizationId}
            permissionList={permissionList}
            writeBlockedProps={writeBlockedProps}
          />

          {perspectives.length > 0 && (
            <>
              <DividerAtom />
              <PerspectiveAssignmentsPanel
                sphere={sphere}
                perspectives={perspectives}
                perspectiveAssignments={
                  initialValues.assignedPerspectives
                }
                isEditing={editable}
              />
            </>
          )}

          {editable && (onSave || onCancel) && (
            <>
              <DividerAtom />
              <div className="sphere-inline-editor__actions">
                {onCancel && (
                  <PrimeReactButton
                    label={t('common.actions.cancel')}
                    variant="secondary"
                    type="button"
                    onClick={onCancel}
                  />
                )}
                {onSave && (
                  <PrimeReactButton
                    label={t('common.actions.save')}
                    icon="pi pi-check"
                    type="submit"
                  />
                )}
              </div>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default SphereInlineEditor;
